import { defineStore } from 'pinia';
import { INotification, ISnackbar } from '@/lib';
import { v4 } from 'uuid';
import { isRevokeRedirectFlowInProgress } from '@/services/api/client/revocation-redirect-state';

export interface IToast {
  text: string;
  type: 'default' | 'success' | 'error';
  buttonLabel?: string;
  buttonClick?: () => void;
  dark?: boolean;
}

interface State {
  notifications: INotification[];
  snackbar: ISnackbar | null;
  toast: IToast | null;
  top: number;
}

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    notifications: [],
    snackbar: null,
    toast: null,
    top: 0
  } as State),
  actions: {
    addNotification(notification: INotification) {
      this.notifications = [
        ...this.notifications,
        {
          ...notification,
          id: v4()
        }
      ];
    },
    addErrorNotification(notification: INotification) {
      if (isRevokeRedirectFlowInProgress()) {
        return;
      }

      this.addNotification({
        ...notification,
        icon: 'warning',
        color: 'danger-600'
      });
    },
    addErrorSnackbar(snackbar: ISnackbar) {
      if (isRevokeRedirectFlowInProgress()) {
        return;
      }

      this.snackbar = {
        autoDismiss: true,
        dismissAfter: 6000,
        color: 'danger',
        icon: 'warning',
        ...snackbar
      };
    },
    setToast(text: string) {
      this.toast = {
        text,
        type: 'default'
      };
    },
    setSuccessToast(text: string) {
      this.toast = {
        text,
        type: 'success'
      };
    },
    setErrorToast(text: string, props: Partial<IToast> = {}) {
      if (isRevokeRedirectFlowInProgress()) {
        return;
      }

      this.toast = {
        ...props,
        text,
        type: 'error'
      };
    },
    addSuccessNotification(notification: INotification) {
      this.addNotification({
        ...notification,
        icon: 'check',
        color: 'primary-500'
      });
    },
    clearNotifications() {
      this.notifications = [];
    },
    clearSnackbar() {
      this.snackbar = null;
    },
    clearToast() {
      this.toast = null;
    },
    clearAllNotifications() {
      this.clearNotifications();
      this.clearSnackbar();
      this.clearToast();
    }
  }
});

